<template>
  <div class="content">
    <base-header class="pb-6">
      <div class="row align-items-center py-4">
        <div class="col-lg-6 col-7">
          <h6 class="h2 text-white d-inline-block mb-0">
            {{ $t("modern_trade.report.expiry_title") }}
          </h6>
          <nav aria-label="breadcrumb" class="d-none d-md-inline-block ml-md-4">
            <route-bread-crumb></route-bread-crumb>
          </nav>
        </div>
      </div>
    </base-header>

    <div class="container-fluid mt--6">
      <card class="no-border-card" body-classes="px-0 pb-1" footer-classes="pb-2">
        <template slot="header">
          <!-- Filters -->
          <div class="row align-items-end mb-3">
            <div class="col-md-3">
              <label class="form-control-label">{{ $t("modern_trade.company.name") }}</label>
              <el-select
                v-model="companyFilter"
                :placeholder="$t('all') || 'All'"
                clearable
                @change="loadData"
                class="w-100"
              >
                <el-option
                  v-for="c in companies"
                  :key="c.id"
                  :label="c.name"
                  :value="c.id"
                ></el-option>
              </el-select>
            </div>
            <div class="col-md-2">
              <label class="form-control-label">{{ $t("modern_trade.report.days_ahead") }}</label>
              <el-select v-model="daysAhead" @change="onDaysAheadChange" class="w-100">
                <el-option
                  v-for="opt in daysAheadOptions"
                  :key="opt"
                  :label="opt + ' ' + $t('modern_trade.report.days_ahead')"
                  :value="opt"
                ></el-option>
              </el-select>
            </div>
            <div class="col-md-5">
              <!-- Bucket filter chips -->
              <label class="form-control-label d-block">Bucket</label>
              <div>
                <span
                  v-for="b in buckets"
                  :key="b.value"
                  class="badge mr-2 cursor-pointer"
                  :class="[b.badgeClass, bucketFilter === b.value ? 'badge-active' : '']"
                  style="cursor: pointer; font-size: 0.8rem; padding: 6px 10px;"
                  @click="toggleBucket(b.value)"
                >
                  {{ b.label }} <span v-if="b.count !== null">({{ b.count }})</span>
                </span>
              </div>
            </div>
            <div class="col-md-2 text-right">
              <base-button size="sm" type="primary" @click="loadData">
                <i class="fa fa-search"></i>
              </base-button>
              <base-button
                size="sm"
                type="success"
                :loading="exporting"
                @click="exportXlsx"
              >
                <i class="fa fa-file-excel"></i> {{ $t("modern_trade.report.export_xlsx") }}
              </base-button>
            </div>
          </div>
        </template>

        <div v-loading="loading">
          <el-table
            :data="rows"
            row-key="id"
            header-row-class-name="thead-light"
          >
            <el-table-column :label="$t('modern_trade.company.name')" min-width="150">
              <template slot-scope="{ row }">{{ row.company_name || row.company_id }}</template>
            </el-table-column>
            <el-table-column label="Product" min-width="180">
              <template slot-scope="{ row }">
                {{ row.product_name || row.product_id }}
                <small v-if="row.barcode" class="d-block text-muted">{{ row.barcode }}</small>
              </template>
            </el-table-column>
            <el-table-column :label="$t('modern_trade.stock.lot_no')" prop="lot_no" min-width="120"></el-table-column>
            <el-table-column :label="$t('modern_trade.stock.expired_date')" prop="expired_date" min-width="110"></el-table-column>
            <el-table-column :label="$t('modern_trade.stock.days_to_expire')" min-width="120" align="center">
              <template slot-scope="{ row }">
                <span :class="daysClass(row.days_to_expire)">
                  {{ row.days_to_expire !== undefined ? row.days_to_expire : "-" }}
                </span>
              </template>
            </el-table-column>
            <el-table-column :label="$t('modern_trade.stock.qty')" prop="qty" min-width="80" align="right"></el-table-column>
            <el-table-column label="Bucket" min-width="120" align="center">
              <template slot-scope="{ row }">
                <span class="badge" :class="bucketBadgeClass(row.bucket)">
                  {{ bucketLabel(row.bucket) }}
                </span>
              </template>
            </el-table-column>
          </el-table>
        </div>

        <div slot="footer" class="d-flex align-items-center justify-content-between">
          <p class="text-muted mb-0">{{ $t("total") }}: {{ total }}</p>
          <base-pagination
            v-model="page"
            :per-page="perPage"
            :total="total"
            @input="loadData"
          ></base-pagination>
        </div>
      </card>
    </div>
  </div>
</template>

<script>
import { Table, TableColumn, Select, Option } from "element-ui";
import RouteBreadCrumb from "@/components/Breadcrumb/RouteBreadcrumb";
import { BasePagination } from "@/components";
import swal from "sweetalert2";
import { mapGetters } from "vuex";
import { ModernTradeService } from "@/resource";

export default {
  name: "MtExpiryReport",
  components: {
    RouteBreadCrumb,
    BasePagination,
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
    [Select.name]: Select,
    [Option.name]: Option,
  },
  computed: {
    ...mapGetters({
      isAccessModernTrade: "Auth/isAccessModernTrade",
    }),
    // round-19 Dev-C: single window + expired flag (Q5). Two buckets only.
    buckets() {
      return [
        { value: "expired", label: this.$t("modern_trade.report.bucket_expired"), badgeClass: "badge-danger", count: this.bucketCounts.expired },
        { value: "active", label: this.$t("modern_trade.report.bucket_active"), badgeClass: "badge-success", count: this.bucketCounts.active },
      ];
    },
  },
  data() {
    return {
      loading: false,
      exporting: false,
      rows: [],
      companies: [],
      total: 0,
      page: 1,
      perPage: 30,
      companyFilter: null,
      // round-19 Dev-C: default 450, options 450/365/180.
      daysAhead: 450,
      daysAheadOptions: [450, 365, 180],
      bucketFilter: null,
      bucketCounts: { expired: null, active: null },
    };
  },
  mounted() {
    this.loadCompanies();
    this.loadData();
  },
  methods: {
    async loadCompanies() {
      try {
        const res = await ModernTradeService.companiesList({ per_page: 200, status: 1 });
        const d = (res.data && res.data.Data && res.data.Data[0]) || {};
        this.companies = d.items || [];
      } catch (e) {
        // non-fatal
      }
    },
    async loadData() {
      this.loading = true;
      try {
        const params = { page: this.page, per_page: this.perPage, days_ahead: this.daysAhead };
        if (this.companyFilter) params.company_id = this.companyFilter;
        if (this.bucketFilter) params.bucket = this.bucketFilter;
        const res = await ModernTradeService.expiryReport(params);
        const d = (res.data && res.data.Data && res.data.Data[0]) || {};
        this.rows = d.items || [];
        this.total = d.total || 0;
      } catch (err) {
        swal.fire({ title: (err.response && err.response.data && err.response.data.Cause) || err.message, icon: "error" });
      } finally {
        this.loading = false;
      }
    },
    async exportXlsx() {
      this.exporting = true;
      try {
        const params = { days_ahead: this.daysAhead };
        if (this.companyFilter) params.company_id = this.companyFilter;
        if (this.bucketFilter) params.bucket = this.bucketFilter;
        const res = await ModernTradeService.expiryReportExport(params);
        const blob = new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `expiry_report_${this.daysAhead}d.xlsx`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      } catch (err) {
        swal.fire({ title: (err.response && err.response.data && err.response.data.Cause) || err.message, icon: "error" });
      } finally {
        this.exporting = false;
      }
    },
    onDaysAheadChange() {
      this.page = 1;
      this.loadData();
    },
    toggleBucket(value) {
      this.bucketFilter = this.bucketFilter === value ? null : value;
      this.page = 1;
      this.loadData();
    },
    daysClass(days) {
      if (days === undefined || days === null) return "";
      if (days < 0) return "text-danger font-weight-bold";
      if (days <= 30) return "text-warning font-weight-bold";
      if (days <= 90) return "text-orange";
      return "";
    },
    bucketLabel(bucket) {
      switch (bucket) {
        case "expired": return this.$t("modern_trade.report.bucket_expired");
        case "active": return this.$t("modern_trade.report.bucket_active");
        default: return bucket || "-";
      }
    },
    bucketBadgeClass(bucket) {
      switch (bucket) {
        case "expired": return "badge-danger";
        case "active": return "badge-success";
        default: return "badge-secondary";
      }
    },
  },
};
</script>

<!-- เคยมีคลาสสีส้มเฉพาะหน้านี้อยู่ตรงนี้ ลบออกแล้ว: ไม่มีใครเรียกใช้เลย
     (bucketBadgeClass คืนแค่ danger / success / secondary) และคู่สีของมัน
     คือขาวบน #fb6340 = 3.02:1 ตกเกณฑ์ AA ⇒ เป็นกับดักที่รอให้ใครมา "reuse"
     ถ้าต้องการชิปสีส้ม ให้เพิ่ม variant เข้า $theme-colors แทน จะได้คู่สี
     ที่ผ่านเกณฑ์อัตโนมัติจาก mixin กลาง

     หมายเหตุ: อย่าเขียนชื่อคลาสรูป b-a-d-g-e-ขีด ลงในคอมเมนต์นี้
     ตัวสแกนคอนทราสต์อ่านชื่อคลาสจากทั้งไฟล์ จะนึกว่ามีคลาสนั้นอยู่จริง
     แล้วรายงานเป็น stale probe -->

